<!-- app/javascript/dashboard/routes/dashboard/bulkmessage/components/modals/CsvMappingModal.vue -->

<script setup>
import { computed, ref, watch } from 'vue';
import { vOnClickOutside } from '@vueuse/components';
import Button from 'dashboard/components-next/button/Button.vue';
import useTextDisplay from '../../utils/useTextDisplay';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  wizardStep: {
    type: Number,
    default: 0,
  },
  mapping: {
    type: Object,
    required: true,
  },
  parsedCsvRows: {
    type: Array,
    default: () => [],
  },
  csvHeaders: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits([
  'update:mapping-phone',
  'update:mapping-name',
  'update:mapping-additional',
  'next-step',
  'previous-step',
  'skip-step',
  'apply-mapping',
  'cancel',
]);

const {
  openFullText,
  getTruncatedText,
  needsTruncation,
  showFullTextModal,
  fullTextContent,
  fullTextTitle,
  closeFullText,
} = useTextDisplay();


const wizardDescription = computed(() => {
  switch (props.wizardStep) {
    case 0:
      return props.mapping.phone
        ? 'Confirme se a coluna é correspondente aos números de telefone dos contatos.'
        : 'Selecione a coluna correspondente aos números de telefone dos contatos.';
    case 1:
      return props.mapping.name
        ? 'Confirme se a coluna é correspondente aos nomes dos contatos. Pode ser a mesma coluna do telefone.'
        : 'Selecione a coluna correspondente aos nomes dos contatos. Pode ser a mesma coluna do telefone.';
    case 2:
      return 'Escolha colunas com informações extras. Você pode usar esses dados para personalizar as mensagens de cada pessoa.';
    default:
      return '';
  }
});

const canGoNext = computed(() => {
  if (props.wizardStep === 0) {
    return !!props.mapping.phone;
  }
  if (props.wizardStep === 1) {
    return !!props.mapping.name;
  }
  return true;
});

const selectedAdditionalColumns = ref([]);
const showInvalidPhonesDropdown = ref(false);
const invalidPhonesButtonRef = ref(null);

// Template refs
const modalContentRef = ref(null);




watch(
  () => props.mapping,
  newMapping => {
    // Only update if there's actually a difference to prevent overwriting local changes
    const newAdditionalColumns = newMapping.additional_columns
      ? (Array.isArray(newMapping.additional_columns) ? [...newMapping.additional_columns] : [])
      : [];

    // Compare arrays to see if there's a real difference
    const currentColumns = selectedAdditionalColumns.value;
    const isDifferent =
      newAdditionalColumns.length !== currentColumns.length ||
      newAdditionalColumns.some((col, index) => col !== currentColumns[index]);

    if (isDifferent) {
      selectedAdditionalColumns.value = newAdditionalColumns;
    } else {
    }
  },
  { deep: true, immediate: true }
);

const isLastStep = computed(() => {
  return props.wizardStep === 2;
});

const toggleColumnSelection = columnName => {
  if (props.wizardStep === 0) {
    // Step 0: Phone selection
    if (props.mapping.phone === columnName) {
      emit('update:mapping-phone', '');
    } else {
      emit('update:mapping-phone', columnName);
    }
  } else if (props.wizardStep === 1) {
    // Step 1: Name selection
    if (props.mapping.name === columnName) {
      emit('update:mapping-name', '');
    } else {
      emit('update:mapping-name', columnName);
    }
  } else if (props.wizardStep === 2) {
    // Step 2: Additional columns selection
    if (
      columnName === props.mapping.phone ||
      columnName === props.mapping.name
    ) {
      return; // Can't select already mapped columns as additional
    }

    const index = selectedAdditionalColumns.value.indexOf(columnName);
    if (index > -1) {
      selectedAdditionalColumns.value.splice(index, 1);
    } else {
      selectedAdditionalColumns.value.push(columnName);
    }

    emit('update:mapping-additional', selectedAdditionalColumns.value);
  }
};

const isColumnSelected = columnName => {
  if (props.wizardStep === 0) {
    return props.mapping.phone === columnName;
  }
  if (props.wizardStep === 1) {
    return props.mapping.name === columnName;
  }
  if (props.wizardStep === 2) {
    if (
      columnName === props.mapping.phone ||
      columnName === props.mapping.name
    ) {
      return true; // Show already mapped columns as selected
    }
    return selectedAdditionalColumns.value.includes(columnName);
  }
  return false;
};

const isPhoneColumn = columnName => {
  return columnName === props.mapping.phone;
};

const isNameColumn = columnName => {
  return columnName === props.mapping.name;
};

const getColumnTooltip = columnName => {
  if (props.wizardStep === 1) {
    if (isPhoneColumn(columnName)) {
      return 'Esta é a coluna do telefone selecionada no passo anterior';
    }
    return `Clique para ${isColumnSelected(columnName) ? 'desselecionar' : 'selecionar'} esta coluna adicional`;
  }
  return '';
};

const getDataCellTooltip = (columnName, cellValue) => {
  const baseTooltip = `${cellValue} - Coluna: ${columnName}`;
  if (props.wizardStep === 1 && isPhoneColumn(columnName)) {
    return `${baseTooltip} (Coluna do telefone)`;
  }
  return `${baseTooltip} - ${getColumnTooltip(columnName)}`;
};

const first5Rows = computed(() => {
  return props.parsedCsvRows.slice(0, 5);
});

const canSelectColumn = columnName => {
  if (props.wizardStep === 0) {
    return true; // Can select any column as phone
  }
  if (props.wizardStep === 1) {
    return true; // Can select any column as name (including phone column)
  }
  if (props.wizardStep === 2) {
    return !isPhoneColumn(columnName) && !isNameColumn(columnName); // Can't select mapped columns
  }
  return false;
};

const detectedPhoneColumns = computed(() => {
  if (!props.parsedCsvRows || props.parsedCsvRows.length === 0) return [];

  return props.csvHeaders.filter(header => {
    const headerLower = String(header).toLowerCase();
    const phoneKeywords = [
      'phone',
      'telefone',
      'tel',
      'celular',
      'mobile',
      'whatsapp',
      'numero',
      'number',
    ];
    const hasPhoneKeyword = phoneKeywords.some(keyword =>
      headerLower.includes(keyword)
    );

    if (hasPhoneKeyword) return true;

    const sampleRows = props.parsedCsvRows.slice(0, 3);
    const phonePatternCount = sampleRows.reduce((count, row) => {
      return count + (looksLikePhoneNumber(row[header]) ? 1 : 0);
    }, 0);

    return phonePatternCount >= 2;
  });
});

// Optimized phone detection with caching
const phoneValidationCache = new Map();

const looksLikePhoneNumber = value => {
  if (!value) return false;
  
  // Convert to string to handle numeric values from Excel
  const stringValue = String(value);

  // Check cache first for performance
  if (phoneValidationCache.has(stringValue)) {
    return phoneValidationCache.get(stringValue);
  }

  const cleaned = stringValue.replace(/[\s\-\(\)\+\.]/g, '');
  const isNumeric = /^\d+$/.test(cleaned);
  const hasReasonableLength = cleaned.length >= 8 && cleaned.length <= 15;
  const result = isNumeric && hasReasonableLength;

  // Cache result but limit cache size for memory management
  if (phoneValidationCache.size < 5000) {
    phoneValidationCache.set(stringValue, result);
  }

  return result;
};

// Auto-select the first detected phone column by default
watch(
  () => props.parsedCsvRows,
  newRows => {
    if (
      newRows &&
      newRows.length > 0 &&
      !props.mapping.phone &&
      detectedPhoneColumns.value.length > 0
    ) {
      emit('update:mapping-phone', detectedPhoneColumns.value[0]);
    }
  },
  { immediate: true }
);

const getColumnHeaderClasses = columnName => {
  const baseClasses =
    'transition-all duration-200 hover:bg-n-slate-3 dark:hover:bg-n-slate-3';

  if (props.wizardStep === 0) {
    // Step 0: Phone selection
    return isColumnSelected(columnName)
      ? `${baseClasses} bg-n-iris-3 dark:bg-n-iris-3 text-n-iris-12 dark:text-n-iris-12`
      : `${baseClasses} bg-n-slate-2 dark:bg-n-slate-2 text-n-slate-12 dark:text-n-slate-12 hover:bg-n-slate-3 dark:hover:bg-n-slate-3`;
  }
  if (props.wizardStep === 1) {
    // Step 1: Name selection
    return isColumnSelected(columnName)
      ? `${baseClasses} bg-n-iris-3 dark:bg-n-iris-3 text-n-iris-12 dark:text-n-iris-12`
      : `${baseClasses} bg-n-slate-2 dark:bg-n-slate-2 text-n-slate-12 dark:text-n-slate-12 hover:bg-n-slate-3 dark:hover:bg-n-slate-3`;
  }
  if (props.wizardStep === 2) {
    // Step 2: Additional columns
    if (isPhoneColumn(columnName)) {
      return `${baseClasses} bg-primary-100 dark:bg-primary-100 text-primary-900 dark:text-primary-900`;
    }
    if (isNameColumn(columnName)) {
      return `${baseClasses} bg-primary-100 dark:bg-primary-100 text-primary-900 dark:text-primary-900`;
    }
    return isColumnSelected(columnName)
      ? `${baseClasses} bg-n-iris-3 dark:bg-n-iris-3 text-n-iris-12 dark:text-n-iris-12`
      : `${baseClasses} bg-n-slate-2 dark:bg-n-slate-2 text-n-slate-12 dark:text-n-slate-12`;
  }

  return `${baseClasses} bg-n-slate-2 dark:bg-n-slate-2 text-n-slate-12 dark:text-n-slate-12`;
};

const getDataCellClasses = columnName => {
  const baseClasses = 'transition-all duration-200';

  if (!canSelectColumn(columnName)) {
    return `${baseClasses} cursor-default opacity-75`;
  }

  if (props.wizardStep === 0) {
    // Step 0: Phone selection
    return isColumnSelected(columnName)
      ? `${baseClasses} cursor-pointer bg-n-iris-2 dark:bg-n-iris-2 hover:bg-n-iris-3 dark:hover:bg-n-iris-3`
      : `${baseClasses} cursor-pointer hover:bg-n-slate-2 dark:hover:bg-n-slate-2`;
  }
  if (props.wizardStep === 1) {
    // Step 1: Name selection
    return isColumnSelected(columnName)
      ? `${baseClasses} cursor-pointer bg-n-iris-2 dark:bg-n-iris-2 hover:bg-n-iris-3 dark:hover:bg-n-iris-3`
      : `${baseClasses} cursor-pointer hover:bg-n-slate-2 dark:hover:bg-n-slate-2`;
  }
  if (props.wizardStep === 2) {
    // Step 2: Additional columns
    if (isPhoneColumn(columnName)) {
      return `${baseClasses} cursor-default bg-primary-50 dark:bg-primary-50`;
    }
    if (isNameColumn(columnName)) {
      return `${baseClasses} cursor-default bg-n-teal-50 dark:bg-n-teal-50`;
    }
    return isColumnSelected(columnName)
      ? `${baseClasses} cursor-pointer bg-n-iris-2 dark:bg-n-iris-2 hover:bg-n-iris-3 dark:hover:bg-n-iris-3`
      : `${baseClasses} cursor-pointer hover:bg-n-slate-2 dark:hover:bg-n-slate-2`;
  }

  return `${baseClasses} cursor-pointer hover:bg-n-slate-2 dark:hover:bg-n-slate-2`;
};

// Helper function to check if a row is essentially empty (all fields empty or just whitespace)
const isRowEssentiallyEmpty = (row) => {
  if (!row) return true;
  
  // Check all columns in the row
  return Object.keys(row).every(key => {
    const value = row[key];
    // Consider null, undefined, empty string, or just whitespace as empty
    return !value || (typeof value === 'string' && value.trim() === '');
  });
};

const validatePhoneColumn = () => {
  if (!props.mapping.phone || !props.parsedCsvRows) return 0;

  // First, filter out all empty rows to work only with actual data
  const nonEmptyRows = props.parsedCsvRows.filter(row => !isRowEssentiallyEmpty(row));
  
  // If we have few rows, just count them all directly (no sampling needed)
  if (nonEmptyRows.length <= 100) {
    return nonEmptyRows.reduce((count, row) => {
      return count + (looksLikePhoneNumber(row[props.mapping.phone]) ? 1 : 0);
    }, 0);
  }
  
  // For performance with large datasets, sample the first 100 non-empty rows
  const sampleRows = nonEmptyRows.slice(0, 100);
  const validCount = sampleRows.reduce((count, row) => {
    return count + (looksLikePhoneNumber(row[props.mapping.phone]) ? 1 : 0);
  }, 0);
  
  // Scale up based on the ratio from our sample of non-empty rows
  const validRatio = validCount / sampleRows.length;
  return Math.round(validRatio * nonEmptyRows.length);
};

const nextStep = () => {
  if (isLastStep.value) {

    // Ensure additional columns are emitted one more time before applying
    emit('update:mapping-additional', selectedAdditionalColumns.value);

    emit('apply-mapping', { skipContactModal: true });
  } else {
    emit('next-step');
  }
};

const previousStep = () => {
  emit('previous-step');
};

const skipStep = () => {
  emit('skip-step');
};

const cancelMapping = () => {
  // Clear cache when cancelling
  phoneValidationCache.clear();
  emit('cancel');
};

const getSelectionStatusClasses = columnName => {
  if (props.wizardStep === 0) {
    // Step 0: Phone selection
    return isColumnSelected(columnName)
      ? 'bg-primary-600 dark:bg-primary-600 text-white hover:bg-primary-800 dark:hover:bg-primary-800 border border-transparent shadow-sm'
      : 'bg-n-slate-3 dark:bg-n-slate-3 text-n-slate-11 dark:text-n-slate-11 hover:bg-n-iris-3 dark:hover:bg-n-iris-3 hover:text-n-iris-12 dark:hover:text-n-iris-12 border border-n-slate-5 dark:border-n-slate-5 hover:border-n-iris-6 dark:hover:border-n-iris-6 shadow-sm';
  }
  if (props.wizardStep === 1) {
    // Step 1: Name selection
    return isColumnSelected(columnName)
      ? 'bg-primary-600 dark:bg-primary-600 text-white hover:bg-primary-800 dark:hover:bg-primary-800 border border-transparent shadow-sm'
      : 'bg-n-slate-3 dark:bg-n-slate-3 text-n-slate-11 dark:text-n-slate-11 hover:bg-n-iris-3 dark:hover:bg-n-iris-3 hover:text-n-iris-12 dark:hover:text-n-iris-12 border border-n-slate-5 dark:border-n-slate-5 hover:border-n-iris-6 dark:hover:border-n-iris-6 shadow-sm';
  }
  if (props.wizardStep === 2) {
    // Step 2: Additional columns
    if (isPhoneColumn(columnName)) {
      return 'bg-primary-500 dark:bg-primary-500 text-white cursor-default border border-transparent shadow-sm';
    }
    if (isNameColumn(columnName)) {
      return 'bg-primary-500 dark:bg-primary-500 text-white cursor-default border border-transparent shadow-sm';
    }
    return isColumnSelected(columnName)
      ? 'bg-primary-600 dark:bg-primary-600 text-white hover:bg-primary-800 dark:hover:bg-primary-800 border border-transparent shadow-sm'
      : 'bg-n-slate-3 dark:bg-n-slate-3 text-n-slate-11 dark:text-n-slate-11 hover:bg-n-iris-3 dark:hover:bg-n-iris-3 hover:text-n-iris-12 dark:hover:text-n-iris-12 border border-n-slate-5 dark:border-n-slate-5 hover:border-n-iris-6 dark:hover:border-n-iris-6 shadow-sm';
  }
  return 'bg-n-slate-3 dark:bg-n-slate-3 text-n-slate-11 dark:text-n-slate-11 border border-transparent shadow-sm';
};

const getSelectionStatusText = columnName => {
  if (props.wizardStep === 0) {
    // Step 0: Phone selection
    return isColumnSelected(columnName) ? 'Selecionado' : 'Selecionar';
  }
  if (props.wizardStep === 1) {
    // Step 1: Name selection
    return isColumnSelected(columnName) ? 'Selecionado' : 'Selecionar';
  }
  if (props.wizardStep === 2) {
    // Step 2: Additional columns
    if (isPhoneColumn(columnName)) {
      return 'Telefone';
    }
    if (isNameColumn(columnName)) {
      return 'Nome';
    }
    return isColumnSelected(columnName) ? 'Selecionado' : 'Adicionar';
  }
  return 'Selecionar';
};

const getInvalidPhoneRows = computed(() => {
  if (!props.mapping.phone || !props.parsedCsvRows) return [];

  // Always limit validation to max 100 rows for performance - consistent with other validation functions
  const maxValidationRows = 100;
  const sampleSize = Math.min(maxValidationRows, props.parsedCsvRows.length);
  const sampleRows = props.parsedCsvRows.slice(0, sampleSize);

  const invalidRows = [];

  for (let i = 0; i < sampleRows.length; i++) {
    const row = sampleRows[i];
    // Skip empty rows - only count as invalid if row has data
    if (isRowEssentiallyEmpty(row)) continue;
    
    if (!looksLikePhoneNumber(row[props.mapping.phone])) {
      invalidRows.push({ ...row, originalIndex: i });
    }
  }

  return invalidRows;
});

const getValidPhoneCount = computed(() => {
  return validatePhoneColumn();
});

const getInvalidPhoneCount = computed(() => {
  if (!props.mapping.phone || !props.parsedCsvRows) return 0;

  // First, filter out all empty rows to work only with actual data
  const nonEmptyRows = props.parsedCsvRows.filter(row => !isRowEssentiallyEmpty(row));
  
  // If we have few rows, just count them all directly (no sampling needed)
  if (nonEmptyRows.length <= 100) {
    return nonEmptyRows.reduce((count, row) => {
      return count + (!looksLikePhoneNumber(row[props.mapping.phone]) ? 1 : 0);
    }, 0);
  }
  
  // For performance with large datasets, sample the first 100 non-empty rows
  const sampleRows = nonEmptyRows.slice(0, 100);
  const invalidCount = sampleRows.reduce((count, row) => {
    return count + (!looksLikePhoneNumber(row[props.mapping.phone]) ? 1 : 0);
  }, 0);
  
  // Scale up based on the ratio from our sample of non-empty rows
  const invalidRatio = invalidCount / sampleRows.length;
  return Math.round(invalidRatio * nonEmptyRows.length);
});

const getPhonesWithoutCountryCode = computed(() => {
  if (!props.mapping.phone || !props.parsedCsvRows) return 0;
  
  // First, filter out all empty rows to work only with actual data
  const nonEmptyRows = props.parsedCsvRows.filter(row => !isRowEssentiallyEmpty(row));
  
  // If we have few rows, just count them all directly (no sampling needed)
  if (nonEmptyRows.length <= 100) {
    return nonEmptyRows.reduce((count, row) => {
      const phone = row[props.mapping.phone];
      if (!phone) return count;
      
      const phoneStr = String(phone).trim();
      // Check if phone starts with +55 or 55
      const hasCountryCode = phoneStr.startsWith('+55') || phoneStr.startsWith('55');
      
      return count + (!hasCountryCode ? 1 : 0);
    }, 0);
  }
  
  // For performance with large datasets, sample the first 100 non-empty rows
  const sampleRows = nonEmptyRows.slice(0, 100);
  const countWithoutCode = sampleRows.reduce((count, row) => {
    const phone = row[props.mapping.phone];
    if (!phone) return count;
    
    const phoneStr = String(phone).trim();
    // Check if phone starts with +55 or 55
    const hasCountryCode = phoneStr.startsWith('+55') || phoneStr.startsWith('55');
    
    return count + (!hasCountryCode ? 1 : 0);
  }, 0);
  
  // Scale up based on the ratio from our sample of non-empty rows
  const withoutCodeRatio = countWithoutCode / sampleRows.length;
  return Math.round(withoutCodeRatio * nonEmptyRows.length);
});

const toggleInvalidPhonesDropdown = () => {
  showInvalidPhonesDropdown.value = !showInvalidPhonesDropdown.value;
};

const closeInvalidPhonesDropdown = () => {
  showInvalidPhonesDropdown.value = false;
};

const formatVariableName = columnName => {
  if (!columnName || typeof columnName !== 'string') {
    return '';
  }
  return '{{' + columnName + '}}';
};

// Watch for changes in selected additional columns
watch(selectedAdditionalColumns, () => {
  // Trigger any necessary updates when columns change
}, { deep: true });
</script>

<template>
  <div v-if="show"
    class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-n-alpha-black75 dark:bg-n-alpha-black75 backdrop-blur-sm">
    <div
      class="relative w-full max-w-5xl overflow-hidden bg-n-solid-1 dark:bg-n-solid-1 border border-n-slate-6 dark:border-n-slate-6 shadow-2xl rounded-xl flex flex-col animate-in fade-in-0 zoom-in-95 duration-300">
      <!-- Header -->
      <div
        class="flex justify-between items-center px-4 pt-3 pb-0 bg-n-slate-2 dark:bg-n-slate-2 rounded-t-xl">
        <div class="flex items-center gap-3">
          <div>
            <h3 class="text-xl font-bold text-n-slate-12 dark:text-n-slate-12">
              Mapear planilha
            </h3>
          </div>
        </div>
        <button
          class="text-n-slate-11 dark:text-n-slate-11 p-2 hover:bg-n-slate-3 dark:hover:bg-n-slate-3 hover:text-n-slate-12 dark:hover:text-n-slate-12 rounded-lg transition-all duration-200"
          @click="cancelMapping">
          <i class="i-lucide-x text-lg" />
        </button>
      </div>

      <!-- Progress Bar -->
      <div class="px-5 py-3 bg-n-slate-2 dark:bg-n-slate-2 border-b border-n-slate-6 dark:border-n-slate-6">
        <div class="flex items-center gap-2 w-full">
          <!-- Step 1: Phone -->
          <div class="flex items-center gap-2">
            <div
              class="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shadow-sm transition-all duration-300"
              :class="[
                wizardStep >= 0
                  ? 'bg-gradient-to-br from-primary-600 to-primary-800 dark:from-primary-600 dark:to-primary-800 text-white'
                  : 'bg-n-slate-4 dark:bg-n-slate-4 text-n-slate-11 dark:text-n-slate-11',
              ]">
              <i v-if="wizardStep > 0" class="i-lucide-check text-sm" />
              <span v-else>1</span>
            </div>
            <span class="text-sm font-semibold text-n-slate-12 dark:text-n-slate-12">Telefone</span>
          </div>

          <!-- Progress bar 1 -->
          <div class="flex-1 h-1.5 mx-2 bg-n-slate-4 dark:bg-n-slate-4 rounded-full overflow-hidden">
            <div
              class="h-full bg-gradient-to-r from-primary-600 to-primary-800 dark:from-primary-600 dark:to-primary-800 rounded-full transition-all duration-500 ease-out"
              :style="`width: ${wizardStep >= 1 ? '100%' : '0%'}`" />
          </div>

          <!-- Step 2: Name -->
          <div class="flex items-center gap-2">
            <div
              class="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shadow-sm transition-all duration-300"
              :class="[
                wizardStep >= 1
                  ? 'bg-gradient-to-br from-primary-600 to-primary-800 dark:from-primary-600 dark:to-primary-800 text-white'
                  : 'bg-n-slate-4 dark:bg-n-slate-4 text-n-slate-11 dark:text-n-slate-11',
              ]">
              <i v-if="wizardStep > 1" class="i-lucide-check text-sm" />
              <span v-else>2</span>
            </div>
            <span class="text-sm font-semibold text-n-slate-12 dark:text-n-slate-12">Nome</span>
          </div>

          <!-- Progress bar 2 -->
          <div class="flex-1 h-1.5 mx-2 bg-n-slate-4 dark:bg-n-slate-4 rounded-full overflow-hidden">
            <div
              class="h-full bg-gradient-to-r from-primary-600 to-primary-800 dark:from-primary-600 dark:to-primary-800 rounded-full transition-all duration-500 ease-out"
              :style="`width: ${wizardStep >= 2 ? '100%' : '0%'}`" />
          </div>

          <!-- Step 3: Additional Columns -->
          <div class="flex items-center gap-2">
            <div
              class="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shadow-sm transition-all duration-300"
              :class="[
                wizardStep >= 2
                  ? 'bg-gradient-to-br from-primary-600 to-primary-800 dark:from-primary-600 dark:to-primary-800 text-white'
                  : 'bg-n-slate-4 dark:bg-n-slate-4 text-n-slate-11 dark:text-n-slate-11',
              ]">
              <span>3</span>
            </div>
            <span class="text-sm font-semibold text-n-slate-12 dark:text-n-slate-12">Adicionais</span>
          </div>
        </div>
      </div>

      <div ref="modalContentRef" class="flex-1 overflow-y-auto px-5 py-4 bg-n-slate-1">
        <!-- Description -->
        <div class="mb-4">
          <div class="bg-n-slate-2 border border-n-slate-6 dark:border-none rounded-lg overflow-visible">
            <!-- Main Description Section -->
            <div class="px-4 py-3">
              <!-- Icon and main text row -->
              <div class="flex items-center gap-3">
                <div class="flex items-center justify-center">
                  <i :class="wizardStep === 0
                      ? 'i-lucide-smartphone'
                      : wizardStep === 1
                        ? 'i-lucide-user'
                        : 'i-lucide-plus-circle'
                    " class="text-woot-600 text-lg" />
                </div>
                <p class="text-base text-n-slate-11 dark:text-n-slate-11 leading-relaxed">
                  {{ wizardDescription }}
                </p>
              </div>
              <!-- Variable usage instructions for step 2 -->
              <div v-if="wizardStep === 2" class="mt-3 space-y-1">
                <p class="text-sm text-n-slate-10 dark:text-n-slate-11">
                  Use <code class="bg-primary-100 dark:bg-primary-400 px-1 rounded font-mono text-xs">&#123;&#123;nome&#125;&#125;</code> para inserir o nome da pessoa e <code class="bg-primary-100 dark:bg-primary-400 px-1 rounded font-mono text-xs">&#123;&#123;telefone&#125;&#125;</code> para inserir o número de telefone
                </p>
              </div>
            </div>
            
            <!-- Phone Validation Summary for Step 0 -->
            <div v-if="wizardStep === 0 && props.mapping.phone">
              <!-- Validation Statistics Section -->
              <div class="border-t border-n-slate-5 dark:border-n-slate-4 px-4 py-2 bg-n-slate-2 dark:bg-n-slate-2">
                <div class="flex items-center gap-3">
                  <i class="i-lucide-list-checks text-grass-700 text-lg" />
                  <div class="flex items-center text-base text-n-slate-11 dark:text-n-slate-11 font-medium">
                      {{ getValidPhoneCount }} números válidos<span v-if="getInvalidPhoneCount > 0">,</span>
                    <div v-if="getInvalidPhoneCount > 0" class="relative inline-block">
                      <button
                        ref="invalidPhonesButtonRef"
                        class="hover:text-n-slate-12 underline text-base"
                        @click="toggleInvalidPhonesDropdown">
                        {{ getInvalidPhoneCount }} inválidos
                      </button>
                      
                      <!-- Invalid Phones Dropdown -->
                      <div v-if="showInvalidPhonesDropdown"
                        v-on-click-outside="closeInvalidPhonesDropdown"
                        class="absolute left-0 top-full mt-2 z-50 w-80 h-80 overflow-y-auto bg-n-alpha-3 backdrop-blur-[100px] border-0 outline outline-1 outline-n-container rounded-xl shadow-lg p-3">
                        <div class="space-y-2">
                          <div v-for="(row, index) in getInvalidPhoneRows" :key="index"
                            class="p-2 bg-n-alpha-1 dark:bg-n-alpha-2 rounded-lg text-sm">
                            <div class="flex items-center justify-between">
                              <span class="text-n-slate-11 dark:text-n-slate-11 font-medium">
                                Linha {{ row.originalIndex + 1 }}:
                              </span>
                              <span class="text-n-amber-11 dark:text-n-amber-11 font-mono text-xs">
                                {{ row[props.mapping.phone] || 'Vazio' }}
                              </span>
                            </div>
                          </div>
                          <div v-if="getInvalidPhoneRows.length === 0" 
                            class="text-center py-4 text-n-slate-10 dark:text-n-slate-10 text-sm">
                            Nenhum número inválido encontrado
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- Warning Section -->
              <!-- Critical Warning: All numbers invalid -->
              <div v-if="getValidPhoneCount === 0 && getInvalidPhoneCount > 0" 
                class="border-t border-n-ruby-5 dark:border-n-ruby-5 bg-n-ruby-2 dark:bg-n-ruby-2 rounded-b-lg">
                <div class="flex items-center gap-3 px-4 py-3">
                  <i class="i-lucide-x-circle text-n-ruby-9 text-lg flex-shrink-0" />
                  <div class="text-n-ruby-11 dark:text-n-ruby-11 text-base">
                    <span class="font-semibold text-n-ruby-11">Atenção:</span> Nenhum número válido encontrado. Certifique-se de incluir o código do país (+55) em todos os números.
                  </div>
                </div>
              </div>
              
              <!-- DDD Warning: Some numbers missing country code -->
              <div v-else-if="getPhonesWithoutCountryCode > 0 && getValidPhoneCount > 0" 
                class="border-t border-n-slate-5 dark:border-n-slate-4 bg-n-slate-2 dark:bg-n-slate-2 rounded-b-lg">
                <div class="flex items-center gap-3 px-4 py-3">
                  <i class="i-lucide-alert-triangle text-n-ruby-9 text-lg flex-shrink-0" />
                  <div class="text-n-slate-11 dark:text-n-slate-11 text-base">
                    <span class="font-semibold text-n-ruby-9">Atenção:</span> {{ getPhonesWithoutCountryCode }} contatos não incluem o código do país (+55) e podem não ser importados corretamente.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>


        <!-- Data Preview -->
        <div v-if="parsedCsvRows.length">
          <div
            class="bg-n-solid-1 dark:bg-n-solid-1 border border-n-slate-6 dark:border-n-slate-6 rounded-lg overflow-hidden shadow-sm">
            <!-- Table -->
            <div class="overflow-x-auto">
              <table class="w-full border-collapse">
                <thead>
                  <tr class="bg-n-slate-2 dark:bg-n-slate-2">
                    <th v-for="h in csvHeaders" :key="h" class="px-2 py-3 text-left font-semibold text-xs" :class="[
                      getColumnHeaderClasses(h),
                      csvHeaders.indexOf(h) === 0 ? 'rounded-tl-lg' : '',
                      csvHeaders.indexOf(h) === csvHeaders.length - 1
                        ? 'rounded-tr-lg'
                        : '',
                    ]" :title="getColumnTooltip(h)">
                      <div class="flex flex-col gap-2 p-2">
                        <div class="flex items-center gap-2">
                          <div class="font-semibold text-sm truncate">
                            {{ h }}
                          </div>
                          <div v-if="detectedPhoneColumns.includes(h)"
                            class="w-4 h-4 bg-n-amber-3 dark:bg-n-amber-3 border border-n-amber-6 dark:border-n-amber-6 rounded flex items-center justify-center">
                            <i class="i-lucide-sparkles text-xs text-n-amber-11 dark:text-n-amber-11" />
                          </div>
                        </div>

                        <!-- Selection Status Card -->
                        <div
                          class="w-full px-3 py-2 rounded-md text-xs font-medium text-center transition-all duration-200 cursor-pointer min-w-[110px]"
                          :class="getSelectionStatusClasses(h)" @click.stop="toggleColumnSelection(h)">
                          {{ getSelectionStatusText(h) }}
                        </div>

                        <!-- Variable Name Preview for Step 2 -->
                        <div v-if="wizardStep === 2 && !isPhoneColumn(h) && !isNameColumn(h)"
                          class="text-xs text-n-slate-10 dark:text-n-slate-10 mt-1 font-mono text-center">
                          <!-- Variável: {{ formatVariableName(h) }} -->
                        </div>
                      </div>
                    </th>
                  </tr>
                </thead>

                <tbody>
                  <tr v-for="(row, rowIndex) in first5Rows" :key="rowIndex" :class="[
                    rowIndex % 2 === 0
                      ? 'bg-n-solid-1 dark:bg-n-solid-1'
                      : 'bg-n-slate-2 dark:bg-n-slate-2',
                  ]">
                    <td v-for="h in csvHeaders" :key="h" class="px-3 py-2 text-sm" :class="getDataCellClasses(h)"
                      :title="getDataCellTooltip(h, row[h])" @click="
                        canSelectColumn(h) ? toggleColumnSelection(h) : null
                        ">
                      <div class="flex items-center justify-between">
                        <div class="flex items-center gap-2 flex-1">
                          <div v-if="looksLikePhoneNumber(row[h])"
                            class="w-4 h-4 bg-n-iris-3 dark:bg-n-iris-3 rounded flex items-center justify-center">
                            <i class="i-lucide-smartphone text-xs text-n-iris-11 dark:text-n-iris-11" />
                          </div>

                          <div class="truncate font-medium text-n-slate-12 dark:text-n-slate-12 flex-1">
                            {{ getTruncatedText(row[h], 25) }}
                          </div>
                        </div>

                        <div class="flex items-center gap-2">
                          <!-- Selection indicator - always reserve space -->
                          <div class="w-2 h-2 rounded-full">
                            <div v-if="isColumnSelected(h)"
                              class="w-2 h-2 bg-primary-600 dark:bg-primary-600 rounded-full" />
                            <div v-else-if="isPhoneColumn(h)"
                              class="w-2 h-2 bg-primary-500 dark:bg-primary-500 rounded-full" />
                          </div>

                          <button v-if="needsTruncation(row[h], 25)"
                            class="px-2 py-1 bg-n-slate-3 dark:bg-n-slate-3 text-n-slate-11 dark:text-n-slate-11 text-sm hover:bg-n-slate-4 dark:hover:bg-n-slate-4 rounded transition-all duration-200"
                            @click.stop="openFullText(h, row[h])">
                            <i class="i-lucide-expand text-sm" />
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>


            <!-- Live Variables Preview -->
            <div v-if="wizardStep === 2" key="live-variables-preview"
              class="bg-gradient-to-r from-n-slate-2 to-n-slate-3 dark:from-n-slate-2 dark:to-n-slate-3 border-t border-n-slate-6 dark:border-n-slate-6 p-4">
              <div class="flex items-center gap-3 text-sm">
                <div class="flex items-center gap-2">
                  <i class="i-lucide-code text-n-slate-11 dark:text-n-slate-11" />
                  <span class="text-n-slate-11 dark:text-n-slate-11 font-semibold">
                    Variáveis disponíveis:
                  </span>
                </div>
                <div class="flex gap-2 flex-wrap">
                  <span
                    class="px-2 py-1 bg-primary-500 dark:bg-n-iris-4 text-white dark:text-black rounded text-xs font-mono shadow-sm"
                    title="Sempre disponível - vem da coluna nome selecionada">
                    {{ formatVariableName('nome') }}
                  </span>
                  <span
                    class="px-2 py-1 bg-primary-500 dark:bg-n-iris-4 text-white dark:text-black rounded text-xs font-mono shadow-sm"
                    title="Sempre disponível - vem da coluna telefone selecionada">
                    {{ formatVariableName('telefone') }}
                  </span>
                  <span v-for="(col, index) in selectedAdditionalColumns" :key="`live-var-${index}-${col}`"
                    class="px-2 py-1 bg-n-iris-8 dark:bg-n-iris-4 text-white dark:text-black rounded text-xs font-mono shadow-sm animate-in fade-in-0 zoom-in-95 duration-200"
                    :title="`Variável criada da coluna: ${col}`">
                    {{ formatVariableName(col) }}
                  </span>
                  <div v-if="selectedAdditionalColumns.length === 0"
                    class="text-xs text-n-slate-10 dark:text-n-slate-10 italic flex items-center gap-1">
                    <i class="i-lucide-info text-xs" />
                    Selecione colunas acima para adicionar mais variáveis
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div
        class="flex justify-between items-center px-5 py-4 border-t border-n-slate-6 dark:border-n-slate-6 bg-n-solid-1 dark:bg-n-solid-1 rounded-b-xl">
        <div>
          <Button v-if="wizardStep > 0" label="← Voltar" variant="outline" color="slate"
            class="transition-all duration-200 hover:scale-105" @click="previousStep" />
        </div>

        <div class="flex gap-2">
          <Button v-if="wizardStep === 2" label="Pular Etapa" variant="outline" color="slate"
            class="transition-all duration-200 hover:scale-105" @click="skipStep" />
          <Button
            :label="isLastStep ? 'Concluir Mapeamento' : (wizardStep === 0 || wizardStep === 1) ? 'Confirmar' : 'Próximo →'"
            :disabled="!canGoNext" class="transition-all duration-200 hover:scale-105" @click="nextStep" />
        </div>
      </div>
    </div>
  </div>

  <!-- Full Text Modal -->
  <div v-if="showFullTextModal"
    class="fixed inset-0 z-50 flex items-center justify-center bg-n-alpha-black75 dark:bg-n-alpha-black75 backdrop-blur-sm">
    <div
      class="relative max-h-full overflow-auto bg-n-solid-1 dark:bg-n-solid-1 border border-n-slate-6 dark:border-n-slate-6 shadow-2xl rounded-lg max-w-lg w-full mx-4 animate-in fade-in-0 zoom-in-95 duration-300">
      <div
        class="flex justify-between items-center px-4 py-3 border-b border-n-slate-6 dark:border-n-slate-6 bg-n-slate-2 dark:bg-n-slate-2 rounded-t-lg">
        <h3 class="text-lg font-semibold text-n-slate-12 dark:text-n-slate-12">
          {{ fullTextTitle }}
        </h3>
        <button
          class="text-n-slate-11 dark:text-n-slate-11 hover:text-n-slate-12 dark:hover:text-n-slate-12 p-1 rounded transition-all duration-200"
          @click="closeFullText">
          <i class="i-lucide-x text-base" />
        </button>
      </div>
      <div class="px-4 py-3">
        <div
          class="overflow-auto max-h-80 border border-n-slate-6 dark:border-n-slate-6 p-3 bg-n-slate-2 dark:bg-n-slate-2 rounded">
          <p class="whitespace-pre-wrap text-n-slate-12 dark:text-n-slate-12">
            {{ fullTextContent }}
          </p>
        </div>
      </div>
      <div
        class="flex justify-end px-4 py-3 border-t border-n-slate-6 dark:border-n-slate-6 bg-n-slate-2 dark:bg-n-slate-2 rounded-b-lg">
        <Button label="Fechar" variant="outline" color="slate" @click="closeFullText" />
      </div>
    </div>
  </div>
</template>
